import { useState, type JSX } from 'react';
import { useStore } from 'zustand';
import { isTimeZone, localTimeZone } from '../../../core/channel';
import {
  CHANNEL_SLUG_PATTERN,
  SCHEDULE_DURATION_MAX,
  SCHEDULE_DURATION_MIN,
  SCHEDULE_ENTRIES_MAX,
  STREAM_CATEGORY_MAX,
  STREAM_TITLE_MAX,
  SUGGESTED_CHANNELS_MAX,
  type FeaturedContent,
  type ScheduleEntry,
} from '../../../core/model';
import { Icon } from '../../components/Icon';
import { useProject, useUiContext } from '../../context';
import { activityStore } from '../../panels/stream-manager/activityStore';
import { attempt, safely } from '../../safe';
import { CountedText } from './StreamInfoForm';

/** Monday first, as a weekly calendar reads; values are 0 = Sunday … 6 = Saturday. */
const WEEK: readonly [number, string, string][] = [
  [1, 'Mon', 'Monday'],
  [2, 'Tue', 'Tuesday'],
  [3, 'Wed', 'Wednesday'],
  [4, 'Thu', 'Thursday'],
  [5, 'Fri', 'Friday'],
  [6, 'Sat', 'Saturday'],
  [0, 'Sun', 'Sunday'],
];
const MINUTE_STEPS = [0, 15, 30, 45] as const;

const pad = (n: number): string => String(n).padStart(2, '0');

/** `HH:MM` for minutes after midnight. */
export function formatStartTime(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** Minutes after midnight for `HH:MM`, or null. */
export function parseStartTime(text: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h < 24 && m < 60 ? h * 60 + m : null;
}

/** "2 h 30 min", "45 min", "3 h". */
export function formatDurationMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Why the draft cannot be saved, or null when it can. */
export function scheduleProblem(entry: ScheduleEntry): string | null {
  if (entry.days.length === 0) return 'Pick at least one day.';
  if (entry.durationMinutes < SCHEDULE_DURATION_MIN || entry.durationMinutes > SCHEDULE_DURATION_MAX) return 'A stream lasts from 15 minutes to 24 hours.';
  if (!isTimeZone(entry.timeZone.trim())) return 'Unknown time zone: use a name such as Europe/Madrid.';
  return null;
}

function ScheduleEditor({ initial, onSave, onCancel }: { initial: ScheduleEntry; onSave(entry: ScheduleEntry): void; onCancel(): void }): JSX.Element {
  const [draft, setDraft] = useState<ScheduleEntry>(initial);
  const [timeText, setTimeText] = useState(formatStartTime(initial.startMinutes));
  const set = (patch: Partial<ScheduleEntry>): void => setDraft((d) => ({ ...d, ...patch }));
  const hours = Math.floor(draft.durationMinutes / 60);
  const minutes = draft.durationMinutes % 60;
  const start = parseStartTime(timeText);
  const problem = start === null ? 'Enter a start time.' : scheduleProblem(draft);
  const zoneBad = !isTimeZone(draft.timeZone.trim());
  const toggleDay = (day: number): void =>
    set({ days: (draft.days.includes(day) ? draft.days.filter((d) => d !== day) : [...draft.days, day]).sort((a, b) => a - b) });

  return (
    <div className="schedule-editor ui-col" role="group" aria-label="Schedule entry">
      <div className="ui-field">
        <label htmlFor="schedule-title">Title</label>
        <CountedText id="schedule-title" label="Schedule title" value={draft.title} max={STREAM_TITLE_MAX} live placeholder="Stream" onCommit={(title) => set({ title })} />
      </div>
      <div className="ui-field">
        <label htmlFor="schedule-category">Category</label>
        <CountedText id="schedule-category" label="Schedule category" value={draft.category} max={STREAM_CATEGORY_MAX} live onCommit={(category) => set({ category })} />
      </div>
      <div className="ui-field">
        <label htmlFor="schedule-start">Start</label>
        <input
          id="schedule-start"
          type="time"
          className="ui-input"
          aria-label="Start time"
          step={60}
          value={timeText}
          onChange={(e) => {
            setTimeText(e.target.value);
            const parsed = parseStartTime(e.target.value);
            if (parsed !== null) set({ startMinutes: parsed });
          }}
        />
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Duration</span>
        <div className="ui-row">
          <select className="ui-select" aria-label="Duration hours" value={hours} onChange={(e) => set({ durationMinutes: Number(e.target.value) * 60 + minutes })}>
            {Array.from({ length: 25 }, (_, h) => (
              <option key={h} value={h}>
                {h} h
              </option>
            ))}
          </select>
          <select className="ui-select" aria-label="Duration minutes" value={minutes} onChange={(e) => set({ durationMinutes: hours * 60 + Number(e.target.value) })}>
            {MINUTE_STEPS.map((m) => (
              <option key={m} value={m}>
                {m} min
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Days</span>
        <div className="ui-row schedule-days" role="group" aria-label="Days">
          {WEEK.map(([day, short, long]) => (
            <button key={day} type="button" className="ui-btn schedule-day" aria-label={long} aria-pressed={draft.days.includes(day)} onClick={() => toggleDay(day)}>
              {short}
            </button>
          ))}
        </div>
      </div>
      <div className="ui-field">
        <label htmlFor="schedule-zone">Time zone</label>
        <input
          id="schedule-zone"
          className={zoneBad ? 'ui-input invalid' : 'ui-input'}
          aria-label="Time zone"
          aria-invalid={zoneBad || undefined}
          autoComplete="off"
          spellCheck={false}
          value={draft.timeZone}
          onChange={(e) => set({ timeZone: e.target.value })}
        />
      </div>
      {problem ? <div className="ui-warn-text">{problem}</div> : null}
      <div className="ui-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="ui-btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="ui-btn primary"
          disabled={problem !== null}
          onClick={() => onSave({ ...draft, startMinutes: start ?? draft.startMinutes, timeZone: draft.timeZone.trim() })}
        >
          Save
        </button>
      </div>
    </div>
  );
}

/** `<API URL>/api/channels/<slug>/schedule.ics`, the feed calendar apps subscribe to. */
export function scheduleIcsUrl(apiUrl: string, slug: string): string {
  return `${apiUrl.trim().replace(/\/+$/, '')}/api/channels/${encodeURIComponent(slug)}/schedule.ics`;
}

function CalendarExport(): JSX.Element {
  const { studio } = useUiContext();
  const apiUrl = useProject((p) => p.output.streaming.swarmApiUrl);
  const slug = useStore(activityStore, (s) => s.channelSlug);
  if (!slug) return <span className="ui-dim">Calendar export: available once ompeer.com knows your channel.</span>;
  const url = scheduleIcsUrl(apiUrl, slug);
  const copy = (): void =>
    void safely(studio, async () => {
      await navigator.clipboard.writeText(url);
      studio.notify('info', 'Calendar link copied.');
    }, 'Could not copy');
  return (
    <div className="ui-row" style={{ flexWrap: 'wrap' }}>
      <span className="ui-dim">
        Calendar export: viewers can subscribe to <span className="ui-selectable ui-mono">{url}</span>
      </span>
      <button type="button" className="ui-btn" aria-label="Copy calendar link" onClick={copy}>
        <Icon name="copy" /> Copy
      </button>
    </div>
  );
}

export function ScheduleSection(): JSX.Element {
  const { studio } = useUiContext();
  const schedule = useProject((p) => p.channel.schedule);
  /** Index being edited, 'new' for an added entry, null when closed. */
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const save = (entries: ScheduleEntry[]): void => void attempt(studio, () => studio.commands.setChannelSchedule(entries));
  const blank = (): ScheduleEntry => ({ title: '', category: '', startMinutes: 20 * 60, durationMinutes: 120, days: [], timeZone: localTimeZone() });

  return (
    <div className="ui-col" style={{ gap: 6 }}>
      {schedule.length === 0 ? <p className="ui-dim">No scheduled streams yet.</p> : null}
      <ul className="schedule-list" aria-label="Schedule">
        {schedule.map((entry, index) =>
          editing === index ? (
            <li key={index}>
              <ScheduleEditor
                initial={entry}
                onCancel={() => setEditing(null)}
                onSave={(next) => {
                  save(schedule.map((e, i) => (i === index ? next : e)));
                  setEditing(null);
                }}
              />
            </li>
          ) : (
            <li key={index} className="schedule-row">
              <div className="schedule-chips" aria-label={`Stream ${index + 1} days`}>
                {WEEK.map(([day, short]) => (
                  <span key={day} className={entry.days.includes(day) ? 'schedule-chip on' : 'schedule-chip'}>
                    {short}
                  </span>
                ))}
              </div>
              <div className="ui-grow ui-col" style={{ gap: 2, minWidth: 0 }}>
                <div className="ui-ellipsis">
                  <strong>{entry.title || 'Stream'}</strong>
                  {entry.category ? <span className="ui-dim"> · {entry.category}</span> : null}
                </div>
                <div className="ui-dim">
                  {formatStartTime(entry.startMinutes)} · {formatDurationMinutes(entry.durationMinutes)} · {entry.timeZone}
                </div>
              </div>
              <button type="button" className="ui-btn" aria-label={`Edit stream ${index + 1}`} disabled={editing !== null} onClick={() => setEditing(index)}>
                Edit
              </button>
              <button type="button" className="ui-icon-btn" aria-label={`Remove stream ${index + 1}`} title="Remove" onClick={() => save(schedule.filter((_, i) => i !== index))}>
                <Icon name="trash" />
              </button>
            </li>
          ),
        )}
      </ul>
      {editing === 'new' ? (
        <ScheduleEditor
          initial={blank()}
          onCancel={() => setEditing(null)}
          onSave={(entry) => {
            save([...schedule, entry]);
            setEditing(null);
          }}
        />
      ) : (
        <div>
          <button type="button" className="ui-btn" disabled={schedule.length >= SCHEDULE_ENTRIES_MAX || editing !== null} onClick={() => setEditing('new')}>
            <Icon name="plus" /> Add stream
          </button>
        </div>
      )}
      <span className="ui-dim">Shown on your channel page in each viewer's time zone.</span>
      <CalendarExport />
    </div>
  );
}

/** Why `slug` cannot be suggested, or null when it can. */
export function slugProblem(slug: string, slugs: readonly string[]): string | null {
  if (!slug) return 'Type a channel name first.';
  if (!CHANNEL_SLUG_PATTERN.test(slug)) return 'Channel names are 1–50 lowercase letters, digits or hyphens.';
  if (slugs.includes(slug)) return 'That channel is already suggested.';
  if (slugs.length >= SUGGESTED_CHANNELS_MAX) return `At most ${SUGGESTED_CHANNELS_MAX} channels.`;
  return null;
}

export function FeaturedSection(): JSX.Element {
  const { studio } = useUiContext();
  const featured = useProject((p) => p.channel.featured);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const set = (patch: Partial<FeaturedContent>): void => void attempt(studio, () => studio.commands.setChannelFeatured(patch));
  const full = featured.suggestedChannels.length >= SUGGESTED_CHANNELS_MAX;
  const add = (): void => {
    const slug = text.trim().toLowerCase();
    const why = slugProblem(slug, featured.suggestedChannels);
    setProblem(why);
    if (why) return;
    set({ suggestedChannels: [...featured.suggestedChannels, slug] });
    setText('');
  };

  return (
    <div className="ui-col" style={{ gap: 8 }}>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Suggested channels</span>
        <div className="ui-col" style={{ gap: 4 }}>
          <div className="ui-row">
            <input
              className={problem ? 'ui-input invalid ui-grow' : 'ui-input ui-grow'}
              aria-label="Suggested channel"
              aria-invalid={problem ? true : undefined}
              placeholder="channel-name"
              autoComplete="off"
              spellCheck={false}
              value={text}
              disabled={full}
              onChange={(e) => {
                setText(e.target.value);
                setProblem(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button type="button" className="ui-btn" disabled={full} onClick={add}>
              <Icon name="plus" /> Add channel
            </button>
            <span className="ui-dim" aria-label="Suggested channel count">
              {featured.suggestedChannels.length}/{SUGGESTED_CHANNELS_MAX}
            </span>
          </div>
          {problem ? <span className="ui-warn-text">{problem}</span> : null}
          {featured.suggestedChannels.length > 0 ? (
            <ul className="stream-tags" aria-label="Suggested channels">
              {featured.suggestedChannels.map((slug) => (
                <li key={slug} className="stream-tag">
                  <span>{slug}</span>
                  <button
                    type="button"
                    className="stream-tag-remove"
                    aria-label={`Remove channel ${slug}`}
                    title="Remove"
                    onClick={() => set({ suggestedChannels: featured.suggestedChannels.filter((s) => s !== slug) })}
                  >
                    <Icon name="close" size={10} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Order</span>
        <div role="radiogroup" aria-label="Suggested channel order" className="ui-col" style={{ gap: 4 }}>
          <label className="ui-check">
            <input type="radio" name="featured-order" checked={featured.suggestedOrder === 'listed'} onChange={() => set({ suggestedOrder: 'listed' })} />
            Show in the order listed
          </label>
          <label className="ui-check">
            <input type="radio" name="featured-order" checked={featured.suggestedOrder === 'personal'} onChange={() => set({ suggestedOrder: 'personal' })} />
            Personalised order per viewer
          </label>
        </div>
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Categories</span>
        <label className="ui-check">
          <input type="checkbox" checked={featured.showRecentCategories} onChange={(e) => set({ showRecentCategories: e.target.checked })} />
          Show recently streamed categories
        </label>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Panel</span>
        <div role="radiogroup" aria-label="Recommendation panel" className="ui-col" style={{ gap: 4 }}>
          <label className="ui-check">
            <input type="radio" name="featured-panel" checked={featured.panel === 'suggested'} onChange={() => set({ panel: 'suggested' })} />
            Suggested channels
          </label>
          <label className="ui-check">
            <input type="radio" name="featured-panel" checked={featured.panel === 'none'} onChange={() => set({ panel: 'none' })} />
            None
          </label>
        </div>
      </div>
    </div>
  );
}
